import React, { useState, useEffect, useReducer, useMemo } from 'react';
import { motion } from 'framer-motion';
import { SHEET_VARIANTS } from '../animations/presets';
import type { PersonalItem } from '../../types';
import { getIconForName } from '../common/IconMap';
import * as aiService from '../../services/ai';
import { useDebounce } from '../../hooks/useDebounce';
import { useData } from '../../contexts/DataContext';
import { useSettings } from '../../contexts/SettingsContext';
import { useUI } from '../../contexts/UIContext';

// Icons
import {
  CloseIcon,
  EditIcon,
  CheckCircleIcon,
  StarIcon,
  TrashIcon,
  SparklesIcon,
  getFileIcon,
  ChevronLeftIcon,
  DownloadIcon,
} from '../icons';

// Details components
import { createInitialEditState, editReducer, AttachmentManager } from '../details/common';
import { TaskView, TaskEdit } from '../details/TaskDetails';
import { BookView, BookEdit } from '../details/BookDetails';
import { WorkoutView, WorkoutEdit } from '../details/WorkoutDetails';
import { HabitView, HabitEdit } from '../details/HabitDetails';
import { AntiGoalView, AntiGoalEdit } from '../details/AntiGoalDetails';
import { GenericView, GenericEdit } from '../details/GenericDetails';
import { inputStyles } from '../details/common';

// Props
interface PersonalItemDetailModalProps {
  item: PersonalItem | null;
  onClose: (nextItem?: PersonalItem) => void;
  onDelete: (id: string) => void;
  onUpdate: (id: string, updates: Partial<PersonalItem>) => void;
  contextItems?: PersonalItem[];
  /** Open in edit mode from the start (used for habits) */
  initialEditMode?: boolean;
}

const PersonalItemDetailModal: React.FC<PersonalItemDetailModalProps> = ({
  item,
  onClose,
  onUpdate,
  onDelete,
  contextItems,
  initialEditMode = false,
}) => {
  const { personalItems, spaces } = useData();
  const { settings } = useSettings();
  const { setHasUnsavedChanges } = useUI();
  const [isEditing, setIsEditing] = useState(initialEditMode);

  const [editState, dispatch] = useReducer(
    editReducer,
    item ? createInitialEditState(item) : createInitialEditState({} as PersonalItem)
  );

  const [isSuggestingIcon, setIsSuggestingIcon] = useState(false);
  const debouncedTitle = useDebounce(editState.title, 500);

  const [relatedItems, setRelatedItems] = useState<PersonalItem[]>([]);
  const [isLoadingRelated, setIsLoadingRelated] = useState(false);

  const personalSpaces = useMemo(() => spaces.filter(s => s.type === 'personal'), [spaces]);
  // ✅ PERF: Memoize projects excluding current item to avoid filtering on every render
  const projectsForSelect = useMemo(
    () => projects.filter(p => p.id !== item?.id),
    [projects, item?.id]
  );

  useEffect(() => {
    if (item) {
      dispatch({ type: 'RESET', payload: item });
      // Keep edit mode if initialEditMode is true (for habits)
      setIsEditing(initialEditMode);

      // Fetch related items
      const fetchRelated = async () => {
        setIsLoadingRelated(true);
        setRelatedItems([]);
        try {
          const related = await aiService.findRelatedPersonalItems(item, personalItems);
          setRelatedItems(related);
        } catch (error) {
          console.error('Failed to find related items:', error);
        } finally {
          setIsLoadingRelated(false);
        }
      };
      fetchRelated();
    }
  }, [item, personalItems]);

  // ✅ PERF: Targeted field comparison instead of JSON.stringify on every keystroke
  // Only compare primitive fields that users typically edit
  useEffect(() => {
    if (isEditing && item) {
      const isDirty =
        editState.title !== (item.title || '') ||
        editState.content !== (item.content || '') ||
        editState.dueDate !== (item.dueDate || '') ||
        editState.dueTime !== (item.dueTime || '') ||
        editState.priority !== (item.priority || 'medium') ||
        editState.icon !== (item.icon || '') ||
        editState.url !== (item.url || '') ||
        editState.author !== (item.author || '');
      setHasUnsavedChanges(isDirty);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [isEditing, item, editState.title, editState.content, editState.dueDate,
    editState.dueTime, editState.priority, editState.icon, editState.url,
    editState.author, setHasUnsavedChanges]);

  useEffect(() => {
    if (isEditing && debouncedTitle && !editState.icon) {
      const suggestIcon = async () => {
        setIsSuggestingIcon(true);
        try {
          const iconName = await aiService.suggestIconForTitle(debouncedTitle);
          dispatch({ type: 'SET_FIELD', payload: { field: 'icon', value: iconName } });
        } catch (error) {
          console.error('Error suggesting icon:', error);
        } finally {
          setIsSuggestingIcon(false);
        }
      };
      suggestIcon();
    }
  }, [debouncedTitle, isEditing, editState.icon]);

  const handleClose = (nextItem?: PersonalItem) => {
    setHasUnsavedChanges(false);
    onClose(nextItem);
  };

  const handleToggleImportant = () => {
    if (item) {
      onUpdate(item.id, { isImportant: !item.isImportant });
    }
  };

  const handleDelete = () => {
    if (item) {
      onDelete(item.id);
      handleClose(); // Close modal after deletion, also clears unsaved flag
    }
  };

  const handleSave = () => {
    if (!item) return;

    const updates: Partial<PersonalItem> = {};
    const originalState = createInitialEditState(item);

    // This logic creates a delta of changes to send for update
    (Object.keys(editState) as Array<keyof typeof editState>).forEach(key => {
      if (JSON.stringify(editState[key]) !== JSON.stringify(originalState[key])) {
        // Safe: editState keys are a subset of PersonalItem keys
        (updates as Record<string, unknown>)[key] = editState[key];
      }
    });

    if (updates.totalPages) updates.totalPages = typeof updates.totalPages === 'string' ? parseInt(updates.totalPages, 10) || 0 : updates.totalPages;

    if (Object.keys(updates).length > 0) {
      onUpdate(item.id, updates);
    }
    setHasUnsavedChanges(false);
    setIsEditing(false);
  };

  const { prevItem, nextItem } = useMemo(() => {
    if (!contextItems || !item) return { prevItem: null, nextItem: null };
    const currentIndex = contextItems.findIndex(i => i.id === item.id);
    if (currentIndex === -1) return { prevItem: null, nextItem: null };
    return {
      prevItem: currentIndex > 0 ? contextItems[currentIndex - 1] : null,
      nextItem: currentIndex < contextItems.length - 1 ? contextItems[currentIndex + 1] : null,
    };
  }, [contextItems, item]);

  const renderViewContent = () => {
    if (!item) return null;
    switch (item.type) {
      case 'task':
        return <TaskView item={item} onUpdate={onUpdate} />;
      case 'book':
        return <BookView item={item} onUpdate={onUpdate} />;
      case 'workout':
        return <WorkoutView item={item} onUpdate={onUpdate} />;
      case 'habit':
        return <HabitView item={item} onUpdate={onUpdate} />;
      case 'antigoal':
        return <AntiGoalView item={item} onUpdate={onUpdate} />;
      case 'roadmap': // Falls through to generic view as it's now handled by its own modal
      case 'note':
      case 'idea':
      case 'journal':
      case 'learning':
      case 'link':
      case 'goal':
      default:
        return <GenericView item={item} onUpdate={onUpdate} />;
    }
  };

  const renderEditContent = () => {
    if (!item) return null;
    switch (item.type) {
      case 'task':
        return <TaskEdit editState={editState} dispatch={dispatch} />;
      case 'book':
        return <BookEdit editState={editState} dispatch={dispatch} />;
      case 'workout':
        return <WorkoutEdit editState={editState} dispatch={dispatch} />;
      case 'habit':
        return <HabitEdit editState={editState} dispatch={dispatch} />;
      case 'antigoal':
        return <AntiGoalEdit editState={editState} dispatch={dispatch} />;
      case 'roadmap': // Falls through to generic edit
      case 'note':
      case 'idea':
      case 'journal':
      case 'learning':
      case 'link':
      case 'goal':
      default:
        return <GenericEdit editState={editState} dispatch={dispatch} />;
    }
  };

  const renderAttachmentsView = () => {
    const attachments = item?.attachments;
    if (!attachments || attachments.length === 0) return null;
    return (
      <div className="border-t border-[var(--border-primary)] pt-6 mt-6">
        <h3 className="text-sm font-semibold text-[var(--accent-highlight)] mb-3 uppercase tracking-wider">
          קבצים מצורפים
        </h3>
        <div className="space-y-3">
          {attachments.map((file, index) => {
            if (file.mimeType.startsWith('image/')) {
              return (
                <div
                  key={index}
                  className="rounded-xl overflow-hidden border border-[var(--border-primary)] bg-black/30"
                >
                  <img
                    src={file.url}
                    alt={file.name}
                    className="max-w-full max-h-96 object-contain mx-auto"
                    loading="lazy"
                  />
                  <div className="p-2 bg-black/50 flex justify-between items-center">
                    <span className="text-xs text-white truncate">{file.name}</span>
                    <a
                      href={file.url}
                      download={file.name}
                      className="text-[var(--dynamic-accent-highlight)] hover:underline text-xs"
                    >
                      הורד
                    </a>
                  </div>
                </div>
              );
            }
            if (file.mimeType.startsWith('video/')) {
              return (
                <div
                  key={index}
                  className="rounded-xl overflow-hidden border border-[var(--border-primary)] bg-black/30"
                >
                  <video controls src={file.url} className="w-full max-h-96" />
                  <div className="p-2 bg-black/50 flex justify-between items-center">
                    <span className="text-xs text-white truncate">{file.name}</span>
                    <a
                      href={file.url}
                      download={file.name}
                      className="text-[var(--dynamic-accent-highlight)] hover:underline text-xs"
                    >
                      הורד
                    </a>
                  </div>
                </div>
              );
            }
            if (file.mimeType.startsWith('audio/')) {
              return (
                <div
                  key={index}
                  className="rounded-xl border border-[var(--border-primary)] bg-black/30 p-3"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-1.5 bg-[var(--dynamic-accent-start)]/20 rounded-lg text-[var(--dynamic-accent-start)]">
                      {getFileIcon(file.mimeType)}
                    </div>
                    <span className="text-sm font-medium text-white truncate flex-1">
                      {file.name}
                    </span>
                  </div>
                  <audio controls src={file.url} className="w-full h-8" />
                </div>
              );
            }
            // Default for PDF, Docs, etc.
            return (
              <a
                key={index}
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                download={file.name}
                className="group flex items-center gap-3 bg-[var(--bg-card)] hover:bg-white/10 p-3 rounded-xl transition-all border border-[var(--border-primary)] hover:border-[var(--dynamic-accent-start)]/50"
              >
                <div className="p-2 bg-[var(--bg-secondary)] rounded-lg text-theme-primary group-hover:text-[var(--dynamic-accent-highlight)] transition-colors">
                  {getFileIcon(file.mimeType)}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-[var(--text-primary)] font-medium truncate block">
                    {file.name}
                  </span>
                  <span className="text-xs text-[var(--text-secondary)]">
                    {Math.round(file.size / 1024)} KB
                  </span>
                </div>
                <DownloadIcon className="w-5 h-5 text-[var(--text-secondary)] group-hover:text-white" />
              </a>
            );
          })}
        </div>
      </div>
    );
  };

  if (!item) return null;

  const Icon = getIconForName(isEditing ? editState.icon : item.icon || 'sparkles');
  const modalBgClass =
    settings.themeSettings.cardStyle === 'glass' ? 'glass-modal-bg' : 'bg-[var(--bg-secondary)]';

  // Full-screen types that need more space for content
  const isFullScreenType = ['workout', 'learning', 'book', 'roadmap', 'habit', 'antigoal'].includes(item.type);
  const heightClass = isFullScreenType ? 'max-h-[85vh]' : 'max-h-[80vh]';

  return (
    <motion.div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={() => handleClose()}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <motion.div
        className={`${modalBgClass} w-full max-w-2xl ${heightClass} rounded-t-3xl shadow-lg flex flex-col border-t border-[var(--border-primary)]`}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="p-item-title"
        variants={SHEET_VARIANTS}
        initial="hidden"
        animate="visible"
        exit="exit"
        drag="y"
        dragControls={undefined}
        dragListener={true}
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={{ top: 0, bottom: 0.5 }}
        onDragEnd={(e, { offset, velocity }) => {
          if (offset.y > 100 || velocity.y > 500) {
            handleClose();
          }
        }}
      >
        <header className="p-4 border-b border-[var(--border-primary)] flex justify-between items-center sticky top-0 bg-transparent backdrop-blur-sm z-10 rounded-t-3xl cursor-grab active:cursor-grabbing">
          {/* Drag Handle */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-1.5 rounded-full bg-white/20" />

          <div className="flex items-center gap-3 overflow-hidden">
            <div className="relative">
              <div
                className={`h-10 w-10 flex items-center justify-center rounded-lg transition-all ${isEditing ? 'bg-[var(--bg-secondary)] text-[var(--text-secondary)]' : 'text-[var(--accent-highlight)]'}`}
              >
                <Icon className="h-6 w-6" />
              </div>
              {isEditing && isSuggestingIcon && (
                <SparklesIcon className="w-4 h-4 text-yellow-400 absolute -top-1 -right-1 animate-pulse" />
              )}
            </div>

            {isEditing ? (
              <input
                type="text"
                id="p-item-title"
                value={editState.title}
                onChange={e =>
                  dispatch({
                    type: 'SET_FIELD',
                    payload: { field: 'title', value: e.target.value },
                  })
                }
                className="bg-transparent text-xl font-bold text-[var(--text-primary)] focus:outline-none rounded-md px-1 -mx-1 flex-1 w-full"
                autoFocus
              />
            ) : (
              <h2
                id="p-item-title"
                className="text-xl font-bold text-[var(--text-primary)] truncate"
              >
                {item.title}
              </h2>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isEditing ? (
              <button
                onClick={handleSave}
                className="flex items-center gap-1.5 text-sm bg-[var(--success)]/20 text-[var(--success)] font-semibold px-3 py-1.5 rounded-lg"
              >
                <CheckCircleIcon className="w-5 h-5" /> <span>שמור</span>
              </button>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="text-[var(--text-secondary)] hover:text-white transition-colors p-2 rounded-full active:scale-95"
              >
                <EditIcon className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={handleToggleImportant}
              className={`p-1 rounded-full transition-colors active:scale-95 ${item.isImportant ? 'text-yellow-400' : 'text-[var(--text-secondary)] hover:text-yellow-400'}`}
            >
              <StarIcon filled={!!item.isImportant} className="h-6 w-6" />
            </button>
            {!isEditing && (
              <button
                onClick={handleDelete}
                className="text-[var(--text-secondary)] hover:text-red-400 transition-colors p-2 rounded-full active:scale-95"
              >
                <TrashIcon className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={() => handleClose()}
              className="text-[var(--text-secondary)] hover:text-white transition-colors p-1 rounded-full active:scale-95"
            >
              <CloseIcon className="h-6 w-6" />
            </button>
          </div>
        </header>

        <div className="p-6 overflow-y-auto flex-grow space-y-6">
          {isEditing && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="spaceId"
                  className="block text-sm font-medium text-[var(--text-secondary)] mb-1"
                >
                  מרחב
                </label>
                <select
                  id="spaceId"
                  value={editState.spaceId}
                  onChange={e =>
                    dispatch({
                      type: 'SET_FIELD',
                      payload: { field: 'spaceId', value: e.target.value },
                    })
                  }
                  className={inputStyles}
                >
                  <option value="">ללא</option>
                  {personalSpaces.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="projectId"
                  className="block text-sm font-medium text-[var(--text-secondary)] mb-1"
                >
                  פרויקט
                </label>
                <select
                  id="projectId"
                  value={editState.projectId}
                  onChange={e =>
                    dispatch({
                      type: 'SET_FIELD',
                      payload: { field: 'projectId', value: e.target.value },
                    })
                  }
                  className={inputStyles}
                >
                  <option value="">ללא</option>
                  {projectsForSelect.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.title}
                      </option>
                    ))}
                </select>
              </div>
            </div>
          )}

          {isEditing ? renderEditContent() : renderViewContent()}

          {isEditing ? (
            <AttachmentManager
              attachments={editState.attachments}
              onAttachmentsChange={atts =>
                dispatch({ type: 'SET_FIELD', payload: { field: 'attachments', value: atts } })
              }
            />
          ) : (
            renderAttachmentsView()
          )}

          {(isLoadingRelated || relatedItems.length > 0) && (
            <div className="border-t border-[var(--border-primary)] pt-6 mt-6">
              <h3 className="text-sm font-semibold text-[var(--accent-highlight)] mb-3 uppercase tracking-wider">
                פריטים קשורים
              </h3>
              {isLoadingRelated ? (
                <p className="text-sm text-[var(--text-secondary)]">AI מחפש חיבורים...</p>
              ) : (
                <div className="space-y-2">
                  {relatedItems.map(related => (
                    <button
                      key={related.id}
                      onClick={() => handleClose(related)}
                      className="w-full text-right bg-[var(--bg-card)] hover:bg-white/5 p-3 rounded-xl transition-colors border border-[var(--border-primary)] hover:border-[var(--dynamic-accent-start)]/50 active:scale-95"
                    >
                      <p className="font-semibold text-[var(--text-primary)] truncate">
                        {related.title}
                      </p>
                      <p className="text-xs text-[var(--text-secondary)] line-clamp-1">
                        {(related.content || '').split('\n')[0]}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {contextItems && (
          <footer className="p-2 border-t border-[var(--border-primary)] sticky bottom-0 bg-[var(--bg-secondary)]/80 backdrop-blur-sm flex justify-between">
            <button
              onClick={() => handleClose(prevItem!)}
              disabled={!prevItem}
              className="p-2 rounded-full text-white disabled:text-theme-disabled"
            >
              <ChevronLeftIcon className="w-6 h-6 transform rotate-180" />
            </button>
            <button
              onClick={() => handleClose(nextItem!)}
              disabled={!nextItem}
              className="p-2 rounded-full text-white disabled:text-theme-disabled"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
          </footer>
        )}
      </motion.div>
    </motion.div>
  );
};

export default React.memo(PersonalItemDetailModal);
